import { SyntheticEvent, useState, useMemo, memo } from "react";
import { Dimmer, Header, Icon, Label, Loader, Tab, TabPane, TabProps } from "semantic-ui-react";

// Commons
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import { Table } from "react-lib/frameworks/Table";
// import CardIsChartWrapper from "./CardIsChartWrapper";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";

// Type
import MainHISController from "@/HIS/MainHISController";
import { RunSequence, SetProp, State, PickedState, LIST_CARD_NAME } from "./MeFormType";

// Interface
import { BTN_ACTS, STYLES, DEFAULT_DIAG_FORM_LIMIT } from "./sequence/MeForm";

// Styles
import "react-table-6/react-table.css";
import "./styles/CardMeFormList.scss";

// Serializers
import DiagRuleSerializer from "issara-sdk/types/DiagRuleSerializer_apps_PRX";

// Type
type CardMeFormListProps = {
  runSequence: RunSequence;
  setProp: SetProp;
  MeFormSequence: State["MeFormSequence"];
  selectedDivisionId: number;
  languageUX: "en" | "th";

  // func
  activeTabIndex: number;
  onChangeTab: (e: SyntheticEvent, data: TabProps) => void;
  onAddForm: () => void;
  onSelectForm: (id: number) => void;
  onPageChange: (type: "active" | "inactive", activePage: number) => void;

  // IsChart
  controller: MainHISController;
  loggedin?: boolean;
  userProfile?: Record<string, any>;
  selectedDivision: any;
} & Pick<PickedState, "buttonLoadCheck" | "loadingStatus">;

type PaneProps = {
  menuItem: { key: string; content: JSX.Element };
  render: () => JSX.Element;
};

const formatFormList = (
  items: DiagRuleSerializer[],
  isActive: boolean,
  handleViewForm: (id?: number) => void,
  handleActivate: (id?: number) => (e: SyntheticEvent) => void,
  handleDeactivate: (id?: number) => (e: SyntheticEvent) => void,
  handleDownloadRaw: (id?: number) => (e: SyntheticEvent) => void,
  setProp: SetProp,
  buttonLoadCheck: any
) => {
  return items.map((item) => ({
    ...item,
    published: (
      <Label
        style={{
          display: "flex",
          justifyContent: "center",
          width: "10rem",
          ...(item.published ? STYLES.published : STYLES.unpublished),
        }}
        content={item.published ? "Published" : "Unpublished"}
      />
    ),
    category_name_list: item.category_name_list?.length ? item.category_name_list.join(", ") : "",
    view_form: (
      <ButtonLoadCheck
        onClick={() => handleViewForm(item.id)}
        style={{
          ...STYLES.primaryThemeBasicBtn,
          width: "10rem",
        }}
        title={
          <div>
            <Icon name="edit" style={{ color: "var(--primary-theme-color)" }} />
            <label style={{ color: "var(--primary-theme-color)", cursor: "pointer" }}>Form</label>
          </div>
        }
      />
    ),
    active_action: isActive ? (
      <ButtonLoadCheck
        setProp={setProp}
        paramKey={`${BTN_ACTS.ACTION_FORM}_${item.id}`}
        buttonLoadCheck={buttonLoadCheck?.[`${BTN_ACTS.ACTION_FORM}_${item.id}`]}
        onClick={handleDeactivate(item.id)}
        basic
        color="grey"
        style={{ width: "10rem" }}
        title={
          <div>
            <Icon name="hide" />
            <label style={{ cursor: "pointer" }}>Deactivate</label>
          </div>
        }
      />
    ) : (
      <ButtonLoadCheck
        setProp={setProp}
        paramKey={`${BTN_ACTS.ACTION_FORM}_${item.id}`}
        buttonLoadCheck={buttonLoadCheck?.[`${BTN_ACTS.ACTION_FORM}_${item.id}`]}
        onClick={handleActivate(item.id)}
        style={{
          ...STYLES.primaryThemeBasicBtn,
          width: "10rem",
        }}
        title={
          <div>
            <Icon name="unhide" style={{ color: "var(--primary-theme-color)" }} />
            <label style={{ color: "var(--primary-theme-color)", cursor: "pointer" }}>
              Activate
            </label>
          </div>
        }
      />
    ),
    download_raw: item.published && (
      <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Icon
          name="download"
          size="big"
          style={{
            color: "var(--primary-theme-color)",
            cursor: "pointer",
          }}
          onClick={handleDownloadRaw(item.id)}
        />
      </div>
    ),
  }));
};

const FormTable = ({ data }: { data: DiagRuleSerializer[] }) => (
  <Table
    className="form-list"
    data={data}
    headers="Status, Form Name, Type, Category, View Form, Action, Raw Data"
    keys="published, name, form_type_label, category_name_list, view_form, active_action, download_raw"
    minRows={9}
    showPagination={false}
    widths="120, ^200, ^120, ^120, 140, 140, 140"
  />
);

const CardMeFormList = (props: CardMeFormListProps) => {
  const handleActivate = (id?: number) => (e: SyntheticEvent) => {
    if (!id) {
      // console.log("[Activate] No ID!!!");
      return;
    }

    props.runSequence({
      sequence: "MeForm",
      action: "ACTION_FORM",
      formId: id,
      data: { active: true },
      division: props.selectedDivisionId,
    });
  };

  const handleDeactivate = (id?: number) => (e: SyntheticEvent) => {
    if (!id) {
      // console.log("[Deactivate] No ID!!!");
      return;
    }

    props.runSequence({
      sequence: "MeForm",
      action: "ACTION_FORM",
      formId: id,
      data: { active: false },
      division: props.selectedDivisionId,
    });
  };

  const handleDownloadRaw = (formId?: number) => (e: SyntheticEvent) => {
    if (!formId) return;

    props.runSequence({
      sequence: "MeForm",
      action: "DOWNLOAD_RAW",
      formId,
    });
  };

  const handleViewForm = (formId?: number) => {
    if (!formId) return;

    props.runSequence({
      sequence: "MeForm",
      action: "GET_FORM",
      formId,
    });

    props.onSelectForm(formId);
  };

  const formattedActiveList = useMemo(() => {
    const items = props.MeFormSequence?.formListPage?.activeList?.items || [];

    return formatFormList(
      items,
      true,
      handleViewForm,
      handleActivate,
      handleDeactivate,
      handleDownloadRaw,
      props.setProp,
      props.buttonLoadCheck
    );
  }, [props.MeFormSequence?.formListPage?.activeList, props.setProp, props.buttonLoadCheck]);

  const formattedInactiveList = useMemo(() => {
    const items = props.MeFormSequence?.formListPage?.inactiveList?.items || [];

    return formatFormList(
      items,
      false,
      handleViewForm,
      handleActivate,
      handleDeactivate,
      handleDownloadRaw,
      props.setProp,
      props.buttonLoadCheck
    );
  }, [props.MeFormSequence?.formListPage?.inactiveList, props.setProp, props.buttonLoadCheck]);

  const panes: PaneProps[] = useMemo(
    () => [
      {
        menuItem: {
          key: "active",
          content: (
            <div style={{ alignItems: "center", width: "10rem", justifyContent: "center" }}>
              <label>Active</label>
            </div>
          ),
        },
        render: () => (
          <TabPane>
            <div>
              <FormTable data={formattedActiveList as any} />

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
                <PaginationShorthand
                  activePage={props.MeFormSequence?.formListPage?.activeList?.activePage || 1}
                  limit={DEFAULT_DIAG_FORM_LIMIT}
                  total={props.MeFormSequence?.formListPage?.activeList?.total}
                  onPageChange={(activePage) => props.onPageChange("active", activePage)}
                />
              </div>
            </div>
          </TabPane>
        ),
      },
      {
        menuItem: {
          key: "inactive",
          content: (
            <div style={{ alignItems: "center", width: "10rem", justifyContent: "center" }}>
              <label>Inactive</label>
            </div>
          ),
        },
        render: () => (
          <TabPane>
            <div>
              <FormTable data={formattedInactiveList as any} />

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
                <PaginationShorthand
                  activePage={props.MeFormSequence?.formListPage?.inactiveList?.activePage || 1}
                  limit={DEFAULT_DIAG_FORM_LIMIT}
                  total={props.MeFormSequence?.formListPage?.inactiveList?.total}
                  onPageChange={(activePage) => props.onPageChange("inactive", activePage)}
                />
              </div>
            </div>
          </TabPane>
        ),
      },
      // {
      //   menuItem: {
      //     key: "chart",
      //     content: (
      //       <div style={{ alignItems: "center", width: "10rem", justifyContent: "center" }}>
      //         <label>Chart</label>
      //       </div>
      //     ),
      //   },
      //   render: () => (
      //     <Tab.Pane>
      //       <div>
      //         <CardIsChartWrapper
      //           controller={props.controller}
      //           loggedin={props.loggedin}
      //           userProfile={props.userProfile}
      //           selectedDivisionIHV3={props.selectedDivision}
      //         />
      //       </div>
      //     </Tab.Pane>
      //   ),
      // },
    ],
    [formattedActiveList, formattedInactiveList]
  );

  return (
    <div className="card-meform-list">
      <Dimmer active={props.loadingStatus?.[LIST_CARD_NAME]} inverted>
        <Loader inverted>Loading</Loader>
      </Dimmer>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
          padding: "0 15px",
        }}
      >
        <Header style={{ color: "rgb(79, 79, 79)", margin: 0 }} as="h2">
          MeForm
        </Header>

        <ButtonLoadCheck
          className="btn-add-form"
          setProp={props.setProp}
          paramKey={BTN_ACTS.ADD_FORM}
          size="medium"
          title={
            <div style={{ display: "flex"}}>
              <Icon name="plus" />
              <label style={{ cursor: "pointer"  }}>Add Form</label>
            </div>
          }
          buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.ADD_FORM]}
          onClick={props.onAddForm}
        />
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          padding: "0 15px"
        }}
      >
        <Label style={{ backgroundColor: "var(--primary-theme-color-half)" }}>
          <div style={{ display: "flex" }}>
            <Icon style={{ color: "var(--primary-theme-color)" }} name="info circle" />
            <label>กด + Add Form เพื่อเริ่มต้นสร้างแบบฟอร์มของคุณได้ทันที</label>
          </div>
        </Label>
      </div>

      <Tab
        activeIndex={props.activeTabIndex}
        menu={{ pointing: true, secondary: true }}
        panes={panes}
        onTabChange={props.onChangeTab}
      />
    </div>
  );
};

CardMeFormList.displayName = "CardMeFormList";

export default memo(CardMeFormList);
